"use client";

import type { SessionCountdown } from "@/hooks/useSessionCountdown";
import { Countdown } from "./Countdown";
import { CalendarLinks } from "./CalendarLinks";
import { WhatsappCta, WhatsappStickyBar } from "./WhatsappCta";
import { MC_JOIN_URL } from "@/lib/config";
import { THANK_YOU } from "@/lib/masterclass";

// ============================================================================
// /mc/ty — place confirmée pour le direct du jeudi.
//   Sans replay, tout se joue sur la présence : la page ne remercie pas, elle
//   fait poser le créneau dans l'agenda et prépare la construction en direct.
// ============================================================================

export function MasterclassThankYou({
  firstName,
  countdown,
  whatsapp,
  onWhatsapp,
}: {
  firstName?: string;
  countdown: SessionCountdown;
  whatsapp: boolean;
  onWhatsapp?: (location: string) => void;
}) {
  const prenom = firstName?.trim();

  return (
    <div className={`mx-auto w-full max-w-xl text-center ${whatsapp ? "pb-24 sm:pb-0" : ""}`}>
      <span className="mb-5 inline-flex items-center rounded-full border border-brand/30 bg-brand/10 px-3 py-1 text-xs font-medium text-brand">
        {THANK_YOU.badge}
      </span>

      <h1 className="text-balance text-2xl font-extrabold sm:text-3xl">
        {THANK_YOU.title}
      </h1>

      <p className="mx-auto mt-4 text-pretty text-ink/70">
        {prenom ? `${prenom}, votre` : "Votre"} place est réservée
        {countdown.ready && !countdown.live ? (
          <>
            {" "}
            pour {countdown.dateLabel} à
            12h30
          </>
        ) : null}
        . Le lien de connexion arrive par email et par message — vérifiez vos
        spams, au cas où.
      </p>

      {/* L'action principale de la page : visible sans défiler, même sur un
          petit écran — et doublée d'une barre collante en bas sur mobile. */}
      {whatsapp && (
        <WhatsappCta prominent className="mt-7" onClick={() => onWhatsapp?.("ty_main")} />
      )}

      <Countdown c={countdown} className="mt-7" />

      {/* Poser le créneau dans l'agenda : c'est ce qui fait la présence. */}
      <CalendarLinks countdown={countdown} className="mt-8" />

      {MC_JOIN_URL && (
        <p className="mt-5 text-sm text-ink/70">
          Le jour J, rejoignez le direct ici :{" "}
          <a
            href={MC_JOIN_URL}
            className="font-semibold text-brand underline underline-offset-2"
          >
            {MC_JOIN_URL}
          </a>
        </p>
      )}

      <div className="mt-8 rounded-2xl border border-ink/10 bg-white p-6 text-left">
        <p className="font-semibold text-ink">
          Préparez ça en 5 minutes — on construit sur vos vrais éléments :
        </p>
        <ol className="mt-4 space-y-4">
          {THANK_YOU.prep.map(([title, detail], i) => (
            <li key={title} className="flex items-start gap-3">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gold text-sm font-bold text-ink">
                {i + 1}
              </span>
              <p className="text-sm text-ink/80">
                <span className="font-semibold text-ink">{title}</span> —{" "}
                {detail}
              </p>
            </li>
          ))}
        </ol>
      </div>

      <p className="mx-auto mt-6 max-w-md text-sm text-ink/70">
        {THANK_YOU.reminder}
      </p>


      <a
        href="/mc"
        className="mt-10 inline-block text-sm text-ink/45 underline underline-offset-2 transition hover:text-ink"
      >
        ← Revenir à la page de la masterclass
      </a>

      <p className="mt-6 text-xs text-ink/45">
        Une heure en direct, sans replay. Si vous ne pouvez plus être présent,
        répondez simplement à l’email de confirmation.
      </p>

      {whatsapp && <WhatsappStickyBar onClick={() => onWhatsapp?.("ty_sticky")} />}
    </div>
  );
}
